import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from "react-router-dom";
import get from 'lodash/get';
import find from 'lodash/find';
import { useAuth } from 'contexts/auth';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import DataTable from 'components/DataTable';
import Button from 'components/Button';
import Papa from 'papaparse';
import { withLoading } from 'utils/hoc';
import { handleErrorResponse, parseJSON } from 'utils/general';
import { deleteDatum, getData } from 'api/general';
import moment from 'moment';

const ID_FIELD = 'product_statistic_id';
const PAGE_OPTIONS = [10, 20, 50, 100];

const ProductStatisticListSection = ({
  title,
  title_font_size,
  title_color,
  table_name,
  id_field,
  columns_config,
  column_mapping_config,
  value_mapping_config,
  odd_row_background_color,
  even_row_background_color,
  odd_row_font_color,
  even_row_font_color,
  header_background_color,
  header_font_color,
  page_options,
  extra_params,
  add_data_path,
  edit_data_path,
  addable,
  editable,
  deletable,
  selectable,
  support_pagination,
  show_data_count,
  container_props,
  setLoading,
  ...props
}) => {
  const { setAuth } = useAuth();
  const [rowsData, setRowsData] = useState([]);

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  const [formattedTableColumns, setFormattedTableColumns] = useState([{
    field: 'date',
    label: 'Date',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {moment(data.date).utc().format('YYYY-MM-DD')}
        </MKTypography>
      </MKBox>;
    }
  }, {
    field: 'hk_folder_count',
    label: 'HK Folder Count',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {data.hk_folder_count}
        </MKTypography>
      </MKBox>;
    }
  }, {
    field: 'hk_raw_message_count',
    label: 'HK Raw Message Count',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {data.hk_raw_message_count}
        </MKTypography>
      </MKBox>;
    }
  }, {
    field: 'hk_message_count',
    label: 'HK Message Count',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {data.hk_message_count}
        </MKTypography>
      </MKBox>;
    }
  }, {
    field: 'hk_product_count',
    label: 'HK Product Count',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {data.hk_product_count}
        </MKTypography>
      </MKBox>;
    }
  }, {
    field: 'hk_uninterested_product_count',
    label: 'HK Uninterested Product Count',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {data.hk_uninterested_product_count}
        </MKTypography>
      </MKBox>;
    }
  }, {
    field: 'hk_incomplete_product_count',
    label: 'HK Incomplete Product Count',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {data.hk_incomplete_product_count}
        </MKTypography>
      </MKBox>;
    }
  }, {
    field: 'us_folder_count',
    label: 'US Folder Count',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {data.us_folder_count}
        </MKTypography>
      </MKBox>;
    }
  }, {
    field: 'us_raw_message_count',
    label: 'US Raw Message Count',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {data.us_raw_message_count}
        </MKTypography>
      </MKBox>;
    }
  }, {
    field: 'us_message_count',
    label: 'US Message Count',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {data.us_message_count}
        </MKTypography>
      </MKBox>;
    }
  }, {
    field: 'us_product_count',
    label: 'US Product Count',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {data.us_product_count}
        </MKTypography>
      </MKBox>;
    }
  }, {
    field: 'us_uninterested_product_count',
    label: 'US Uninterested Product Count',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {data.us_uninterested_product_count}
        </MKTypography>
      </MKBox>;
    }
  }, {
    field: 'us_incomplete_product_count',
    label: 'US Incomplete Product Count',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {data.us_incomplete_product_count}
        </MKTypography>
      </MKBox>;
    }
  }]);
  const navigate = useNavigate();

  const fetchDataFromApi = useCallback(() => {
    setLoading(true);
    const dataParams = {
      ...(sortBy ? {
        $orderBy: `${sortBy} ${sortDirection}`,
      } : {}),
      $skip: currentPage * pageSize,
      $top: pageSize,
    };
    return getData('/product_statistics', dataParams, 'bearer')
      .then(({ data, headers }) => {
        const contentRange = get(headers, 'content-range');
        const size = Number(contentRange.split('/')[1]);
        setRowsData(data);
        setTotalRows(size);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      }).finally(() => {
        setLoading(false);
      });
  }, [currentPage, pageSize, setAuth, setLoading, sortBy, sortDirection]);

  useEffect(() => {
    fetchDataFromApi();
  }, [fetchDataFromApi]);

  const onClickSort = useCallback((headerName) => {
    const column = find(formattedTableColumns, ['field', headerName]);
    if (column && !column.is_non_sortable) {
      if (sortBy === headerName) {
        setSortDirection((previousSortDirection) => (previousSortDirection === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(headerName);
        setSortDirection('asc');
      }
    }
  }, [formattedTableColumns, sortBy]);

  const downloadExcel = useCallback(() => {
    setLoading(true);
    const dataParams = {
      ...(sortBy ? {
        $orderBy: `${sortBy} ${sortDirection}`,
      } : {}),
    };
    return getData('/product_statistics', dataParams, 'bearer')
      .then(({ data }) => {
        const csvRows = data.map((datum) => {
          const row = {};
          formattedTableColumns.forEach(({ field, label }) => {
            row[label] = field === 'date' ? moment(datum.date).utc().format('YYYY-MM-DD') : datum[field];
          });
          return row;
        });
        let csv = Papa.unparse(csvRows);
        csv = `\uFEFF\r\n${csv}`;
        const csvData = new Blob([csv], { type: 'text/csv' });
        const csvURL = URL.createObjectURL(csvData);
        const link = document.createElement('a');
        link.href = csvURL;
        link.download = `product_statistics_${moment().format('YYYY-MM-DD_h-mm-A')}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(csvURL);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [formattedTableColumns, setAuth, setLoading, sortBy, sortDirection]);

  return <MKBox
    sx={{
      ...parseJSON(container_props, {}),
    }}
    {...props}
  >

    <MKBox mb={2} display="flex" alignItems="center" justifyContent="space-between">
      <MKTypography mr="1rem" color={title_color == null ? '#000' : title_color} fontSize="20px">
        Product Statistics
      </MKTypography>
      <Button style={{ width: 'auto' }} onClick={downloadExcel}>
        Download Excel
      </Button>
    </MKBox>

    <DataTable
      data={rowsData}
      columns={formattedTableColumns}
      headerBackgroundColor={header_background_color == null ? '#f2f3f7' : header_background_color}
      headerFontColor={header_font_color == null ? '#0094ff' : header_font_color}
      oddRowBackgroundColor={odd_row_background_color == null ? '#eeeeee' : odd_row_background_color}
      oddRowFontColor={odd_row_font_color == null ? '#000' : odd_row_font_color}
      evenRowBackgroundColor={even_row_background_color == null ? '#fff' : even_row_background_color}
      evenRowFontColor={even_row_font_color == null ? '#000' : even_row_font_color}
      idField={ID_FIELD}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSort={onClickSort}
      paginationDisabled={null}
      onClickRow={null}
      onPressAdd={null}
      onPressEdit={null}
      onPressDelete={null}
      selectable={true}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      rowsPerPage={pageSize}
      rowsPerPageOptions={PAGE_OPTIONS}
      totalCount={totalRows}
      onRowsPerPageChange={setPageSize}
    />

  </MKBox>;
};

ProductStatisticListSection.propTypes = {
  title_font_size: PropTypes.string,
  header_color: PropTypes.string,
  text_color: PropTypes.string,
  page_options: PropTypes.string,
  setLoading: PropTypes.func,
  show_header: PropTypes.bool,
  extra_params: PropTypes.object,
  sort_by_options: PropTypes.array,
  currency_options: PropTypes.array,
  source_options: PropTypes.array,
};

ProductStatisticListSection.defaultProps = {
  title_font_size: '',
  header_color: '#2c589d',
  text_color: '#000',
  page_options: [],
  setLoading: null,
  show_header: false,
  extra_params: {},
  sort_by_options: [],
  currency_options: [],
  source_options: [],
};

export default withLoading(ProductStatisticListSection);
